import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { I18n } from 'react-redux-i18n';
import { Input, Segmented, Table, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';

import ExportButton from 'components/Export';
import ColumnVisibilityModal from 'components/ColumnVisibilityModal';
import NewCard from '../Card';
import TabNavigation from '../components/TabNavigation';
import StoreModal from '../../Distribution/components/StoreModal';

import {
  getWinesAndBrandsColumns,
  getPosColumns,
  getPositioningColumns
} from './column_definitions';

import { FiltersTypes } from 'interface/Distribution/globalView';
import CardHeader from 'components/CardHeader';
import i18n from 'i18n';
import { useDistributionGlobalDataFilters } from 'apiTyped/hooks/global_data_view.query';

const { Text } = Typography;

type GlobalDataTableProps = {
  data: any[];
  activeTab?: string;
  filters: FiltersTypes;
  currency: string;
  isLoading: boolean;
  // eslint-disable-next-line no-unused-vars
  setTableTypeActive: (e: string) => void;
  exportUrl: string;
  exportParams: Record<string, any>;
  posCount?: number;
  // eslint-disable-next-line no-unused-vars
  onChangeFilters: (e: any) => void;
  onClearFilters: () => void;
  // eslint-disable-next-line no-unused-vars
  setSearch: (e: string) => void;
};

const GlobalDataTable = ({
  data,
  filters,
  currency,
  isLoading,
  setTableTypeActive,
  exportUrl,
  exportParams,
  setSearch
}: GlobalDataTableProps) => {
  const { filters: apiFilters } = useDistributionGlobalDataFilters({
    network: filters.network as 'on' | 'off',
    premium_store: false,
    survey_year: filters.surveDate === 'flow' ? 'current' : filters.surveDate
  });
  const canAccessStorePopup = true;

  const tableTypes = useMemo(() => {
    const types = ['point_of_sale', 'positioning', 'wine', 'brand'];
    if (filters.wineType === 'spirit') {
      types[0] = 'spirit';
    }
    return types;
  }, [filters.wineType]);

  const [pagination, setPagination] = useState(20);
  const [localSearch, setLocalSearch] = useState(filters.search);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<Record<string, string[]>>(
    {}
  );

  // Sync local search with filters when they change externally (e.g., clear filters)
  useEffect(() => {
    setLocalSearch(filters.search);
  }, [filters.search]);

  // Create a debounced version of setSearch
  const debouncedSetSearch = useRef(
    debounce((value: string) => {
      setSearch(value);
    }, 400)
  ).current;

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLocalSearch(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch]
  );

  const top_wines_and_brands_columns = getWinesAndBrandsColumns(
    filters,
    currency
  );

  const hasClientCustomRefs = useMemo(() => {
    return (
      apiFilters?.client_custom_refs && apiFilters.client_custom_refs.length > 0
    );
  }, [apiFilters]);

  // Show rival_custom_refs column when client has custom brands configured
  const hasRivalCustomRefs = hasClientCustomRefs;

  const handleStoreClick = useCallback((storeId: string) => {
    setSelectedStoreId(storeId);
    setIsModalOpen(true);
  }, []);

  const pos_columns = getPosColumns(
    hasClientCustomRefs,
    hasRivalCustomRefs,
    canAccessStorePopup ? handleStoreClick : undefined
  );

  const positioning_columns = getPositioningColumns();

  const getActiveColumns = () => {
    if (['brand', 'wine', 'spirit'].includes(filters.tableTypeActive)) {
      return top_wines_and_brands_columns;
    }
    if (filters.tableTypeActive === 'point_of_sale') {
      return pos_columns;
    }
    return positioning_columns;
  };

  const activeColumns: any[] = getActiveColumns();

  const getDefaultHiddenColumns = (columns: any[]) => {
    return columns
      .filter((col: any) => col.hiddenByDefault && col.name)
      .map((col: any) => col.name);
  };

  const currentHiddenColumns =
    hiddenColumns[filters.tableTypeActive] ??
    getDefaultHiddenColumns(activeColumns);
  const columnsNames = activeColumns
    .filter((col: any) => col.name)
    .map((col: any) => col.name);
  const visibleColumns = activeColumns.filter(
    (col: any) => !col.name || !currentHiddenColumns.includes(col.name)
  );

  const handleApplyHiddenColumns = (newHiddenColumns: string[]) => {
    setHiddenColumns((prev) => ({
      ...prev,
      [filters.tableTypeActive]: newHiddenColumns
    }));
  };

  const getSubtitleText = (tableType: string, paginationValue: number) => {
    const baseText = `Top ${paginationValue}`;
    const isSpiritSelected = filters.wineType === '2';

    switch (tableType) {
      case 'wine':
        // When spirit category is selected and viewing wine table, show spirit subtitle
        if (isSpiritSelected) {
          return `${baseText} ${I18n.t(
            'globalView.kpi.subTitle.subTitleSpirit'
          )}`;
        }
        return `${baseText} ${I18n.t('globalView.kpi.subTitle.subTitleWine')}`;
      case 'brand':
        return `${baseText} ${I18n.t('globalView.kpi.subTitle.subTitleBrand')}`;
      case 'spirit':
        return `${baseText} ${I18n.t(
          'globalView.kpi.subTitle.subTitleSpirit'
        )}`;
      case 'point_of_sale':
        return `${baseText} ${I18n.t(
          'globalView.kpi.subTitle.subTitlePointOfSale'
        )}`;
      case 'positioning':
        return `${I18n.t('globalView.kpi.subTitle.subTitlePositioning')}`;
      default:
        return '';
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStoreId(null);
  };

  return (
    <>
      <NewCard
        header={
          <TabNavigation
            filters={filters}
            tableTypes={tableTypes}
            onSelect={setTableTypeActive}
          />
        }
        actions={
          <Input
            size="middle"
            placeholder={I18n.t(
              {
                wine: 'globalView.filters.searchPlaceholderWine',
                spirit: 'globalView.filters.searchPlaceholderWine',
                brand: 'globalView.filters.searchPlaceholderBrand',
                point_of_sale: 'globalView.filters.searchPlaceholderPos',
                positioning: 'globalView.filters.searchPlaceholderPositioning'
              }[filters.tableTypeActive] ||
                'globalView.filters.searchPlaceholder'
            )}
            className="filter-input"
            suffix={<SearchOutlined />}
            value={localSearch}
            onChange={handleSearchChange}
          />
        }
      >
        <div className="kpi-content">
          <CardHeader
            title={
              <Text
                type="secondary"
                // @ts-expect-error style
                styles={{ fontSize: 16, fontFamily: 'Oswald' }}
              >
                {getSubtitleText(filters.tableTypeActive, pagination)}
              </Text>
            }
            fontSize="20px"
            alignItems="center"
            styles={`.cardHeaderRight{width:100%;}`}
            iconRight="right"
            rightComponents={
              <div className="rightComponentsHeader">
                <div className="exportButtonContainer">
                  <ColumnVisibilityModal
                    columnsNames={columnsNames}
                    hiddenColumns={currentHiddenColumns}
                    onApply={handleApplyHiddenColumns}
                  />
                  <ExportButton
                    path={exportUrl}
                    options={exportParams}
                    name={`global-data-${filters.tableTypeActive}`}
                    xls
                    newApi
                  />
                </div>
              </div>
            }
          />

          <Table
            columns={visibleColumns}
            dataSource={data.slice(0, pagination)}
            pagination={false}
            loading={isLoading}
            bordered
            scroll={{ x: 'max-content' }}
            sticky={{ offsetHeader: 0 }}
            locale={{
              triggerDesc: I18n.t('globalView.table.triggerDesc'),
              triggerAsc: I18n.t('globalView.table.triggerAsc'),
              cancelSort: I18n.t('globalView.table.cancelSort'),
              emptyText: <>{I18n.t('globalView.table.noData')}</>
            }}
          />
        </div>
      </NewCard>
      <div className="paginationContainer">
        <Segmented
          options={[
            { label: 'Show 20', value: 20 },
            {
              label: 'Show 50',
              value: 50,
              disabled: data.length <= 20
            },
            {
              label: 'Show 100',
              value: 100,
              disabled: data.length <= 50
            }
          ]}
          name="group"
          onChange={setPagination}
        />
      </div>

      <StoreModal
        storeId={selectedStoreId}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        displayDocumentsTab={true}
      />
    </>
  );
};

export default GlobalDataTable;
